import {
    type GlobalAddressMode,
    type GlobalAddressResult,
} from "@interfaces/address/globalAddress";
import { QueryKeys } from "@lib/enums/QueryKeys";
import { ClientFetchService } from "@lib/helpers/clientFetchService";
import { useApi } from "@lib/helpers/useApi";
import { getQueryClient } from "@lib/helpers/queryClient";

type GetUniqueAddressArgs = {
    country: string;
    mode?: GlobalAddressMode;
    addressId: string;
    locale?: string;
};

type GetGlobalAddressesArgs = {
    searchValue?: {
        address1?: string;
        postalCode?: string;
    };
    country: string;
    mode?: GlobalAddressMode;
    addressId?: string;
    locale?: string;
    searchRef?: string;
};

type GlobalAddressKeyFactoryArgs = GetUniqueAddressArgs & GetGlobalAddressesArgs;

const fetchUniqueAddress = async ({ country, mode, addressId, locale }: GetUniqueAddressArgs) => {
    return await ClientFetchService.post<GetGlobalAddressesArgs, GlobalAddressResult>(
        "/api/checkout/global-addresses",
        {
            searchValue: {},
            country,
            mode,
            addressId,
            locale,
        }
    );
};

export const getUniqueAddressAsync = async ({
    country,
    mode,
    addressId,
    locale,
}: GetUniqueAddressArgs) => {
    const queryClient = getQueryClient();
    const key = getAddressSearchQueryKey({ searchValue: {}, country, mode, locale, addressId });
    return queryClient.ensureQueryData({
        queryKey: key,
        queryFn: () => fetchUniqueAddress({ country, mode, addressId, locale }),
        staleTime: 0,
    });
};

const fetchGlobalAddresses = async ({
    searchValue,
    country,
    mode,
    locale,
    searchRef,
}: GetGlobalAddressesArgs): Promise<GlobalAddressResult> => {
    return await ClientFetchService.post<GetGlobalAddressesArgs, GlobalAddressResult>(
        "/api/checkout/global-addresses",
        {
            searchValue,
            country,
            mode,
            locale,
            searchRef,
        }
    );
};

const getAddressSearchQueryKey = ({
    searchValue,
    country,
    mode,
    locale,
    addressId,
    searchRef,
}: Partial<GlobalAddressKeyFactoryArgs>) => [
    QueryKeys.getGlobalAddresses,
    searchValue ?? {},
    country ?? "",
    mode ?? "",
    locale ?? "",
    addressId ?? "",
    searchRef ?? "",
];

type UseAddressSearchArgs = GetGlobalAddressesArgs & {
    enabled?: boolean;
    staleTime?: number;
};

export const useAddressSearch = ({
    searchValue,
    country,
    mode,
    enabled = true,
    staleTime = 0,
    locale,
    searchRef,
}: UseAddressSearchArgs) => {
    return useApi.useAsync(
        getAddressSearchQueryKey({ searchValue, country, mode, locale, searchRef }),
        () =>
            fetchGlobalAddresses({
                searchValue,
                country,
                mode,
                locale,
                searchRef,
            }),
        {
            staleTime,
            enabled,
        }
    );
};

export const getAddressSearchAsync = async ({
    searchValue,
    country,
    mode,
    locale,
}: GetGlobalAddressesArgs): Promise<GlobalAddressResult> => {
    const queryClient = getQueryClient();
    const key = getAddressSearchQueryKey({ searchValue, country, mode, locale });
    return queryClient.ensureQueryData({
        queryKey: key,
        queryFn: () => fetchGlobalAddresses({ searchValue, country, mode, locale }),
        staleTime: 0,
    });
};
